// The seller's thumbprint from a USB fingerprint scanner.
//
// SecuGen scanners (Hamster Pro 20, Hamster Plus, Hamster IV...) work in the browser through
// SecuGen's free "WebAPI" app (SgiBioSrv) installed on the counter computer. This page asks
// that app, at https://localhost:8443 on the same computer, for one capture and gets back a
// picture of the print. Nothing goes over the internet. Any other scanner works through
// "Upload scan": save the print from the scanner's own program and pick the file.

export type ScanProblem = 'not_found' | 'no_finger' | 'busy' | 'license' | 'failed';

export class ScanError extends Error {
  constructor(message: string, public problem: ScanProblem) {
    super(message);
  }
}

// Newer SecuGen WebAPI apps listen on 8443; older ones on 8000.
const SERVICES = ['https://localhost:8443', 'https://localhost:8000'];

function problemFor(code: number): ScanError {
  if (code === 54) return new ScanError('The scanner didn’t feel a thumb. Ask the seller to press their right thumb flat on the glass, then tap Scan again.', 'no_finger');
  if (code === 55) return new ScanError('The scanner isn’t plugged in, or this computer doesn’t see it. Plug it in and try again.', 'not_found');
  if (code === 59) return new ScanError('The scanner is busy. Wait a second and try again.', 'busy');
  if (code >= 10001 && code <= 10004) return new ScanError('SecuGen’s scanner app needs its license for this website. The owner adds it in Settings → Buy & trade-in.', 'license');
  return new ScanError(`The scanner had a problem (code ${code}). Try again, or tap Upload scan.`, 'failed');
}

/** SecuGen sends a BMP; receipts and the server want PNG. */
async function bmpToPng(base64: string): Promise<string> {
  const img = new Image();
  img.src = `data:image/bmp;base64,${base64}`;
  await img.decode();
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const ctx = c.getContext('2d');
  if (!ctx) throw new ScanError('This computer can’t prepare the thumbprint picture.', 'failed');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, 0, 0);
  return c.toDataURL('image/png');
}

/** Waits for the seller's thumb on the scanner (up to `timeoutMs`) and returns the print as a PNG. */
export async function scanThumbprint(opts: { license?: string; timeoutMs?: number } = {}): Promise<{ dataUrl: string; quality: number | null; scanner: string | null }> {
  const wait = opts.timeoutMs ?? 10_000;
  // The same request SecuGen's own sample page sends (form fields in a plain-text body).
  const body = new URLSearchParams({ Timeout: String(wait), Quality: '50', licstr: opts.license ?? '', templateFormat: 'ISO', imageWSQRate: '0.75' }).toString();
  for (const base of SERVICES) {
    let res: Response;
    try {
      res = await fetch(`${base}/SGIFPCapture`, { method: 'POST', body, signal: AbortSignal.timeout(wait + 8_000) });
    } catch {
      continue; // nothing listening there (or the browser blocked it): try the next address
    }
    const j = await res.json().catch(() => null);
    if (!j || typeof j !== 'object') continue;
    const code = Number(j.ErrorCode);
    if (code === 0 && typeof j.BMPBase64 === 'string' && j.BMPBase64) {
      return { dataUrl: await bmpToPng(j.BMPBase64), quality: Number.isFinite(Number(j.ImageQuality)) ? Number(j.ImageQuality) : null, scanner: j.Model ? String(j.Model) : null };
    }
    throw problemFor(code);
  }
  throw new ScanError(
    'No fingerprint scanner found. Plug in the scanner and make sure SecuGen’s WebAPI app is running on this computer. If Chrome asks to let STORE OS use apps on this device, choose Allow. Or tap Upload scan.',
    'not_found',
  );
}
